import type { BaseAPIResponse } from '~/types/api';
import { ApiError } from '~/utils/errors';

export interface Passkey {
    id: number;
    device_name: string | null;
    created_at: string;
    last_used_at: string | null;
}

export interface PasskeyRegistrationOptions {
    challenge: string;
    rp: {
        name: string;
        id: string;
    };
    user: {
        id: string;
        name: string;
        displayName: string;
    };
    pubKeyCredParams: Array<{ type: string; alg: number }>;
    authenticatorSelection: {
        authenticatorAttachment: string | null;
        userVerification: string;
        residentKey: string;
    };
    attestation: string;
    timeout: number;
    excludeCredentials: Array<{ id: string; type: string }>;
    _stateToken: string; // Encrypted state token for stateless verification
}

// Pure API service for passkey operations
export const passkeyService = {
    async getRegistrationOptions(email: string): Promise<PasskeyRegistrationOptions> {
        const nuxtApp = useNuxtApp();

        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<PasskeyRegistrationOptions>>(
                nuxtApp.$api.routes.auth.passkeys.registerOptions(),
                {
                    method: 'POST',
                    body: { email },
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to get registration options', status);
            }

            // Handle both wrapped and unwrapped responses
            const options = (data as any)?.data || data;
            
            if (!options || !options.challenge) {
                console.error('Invalid registration options response:', data);
                throw new ApiError('Invalid registration options received from server', 500);
            }

            return options as PasskeyRegistrationOptions;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async registerPasskey(credential: unknown, stateToken: string, deviceName?: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        if (!credential) {
            throw new Error('Credential is required');
        }

        if (!stateToken) {
            throw new Error('State token is required');
        }

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.auth.passkeys.register(),
                {
                    method: 'POST',
                    body: {
                        credential,
                        stateToken,
                        device_name: deviceName,
                    },
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to register passkey', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getPasskeys(): Promise<Passkey[]> {
        const nuxtApp = useNuxtApp();

        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<Passkey[]>>(
                nuxtApp.$api.routes.me.passkeys.get(),
                {
                    method: 'GET',
                }
            );

            if (status !== 200 || !data?.data) {
                throw new ApiError(message || 'Failed to fetch passkeys', status);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async deletePasskey(passkeyId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.me.passkeys.delete(passkeyId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to delete passkey', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },
};

